import { useEffect, useRef } from 'react';
import { COACH } from '@/constants/coach';

/**
 * Renders the running Q&A history with the coach — learner asks + coach replies,
 * accumulated as the user converses. Distinct from the roleplay
 * `TranscriptTurn` shape (which carries `at_s` for scoring); this one is
 * just-the-words chat history.
 *
 * The container is height-capped and scrolls internally; we auto-pin to the
 * bottom whenever a new turn is appended so the latest message is always in
 * view. Older turns scroll up out of sight; the user can scroll up to read
 * history.
 *
 * Empty turns list → nothing rendered.
 */

export interface ChatTurn {
  id: string;
  role: 'learner' | 'coach';
  text: string;
}

export default function ChatTranscript({ turns }: { turns: ChatTurn[] }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Pin to bottom on every turn append.
    el.scrollTop = el.scrollHeight;
  }, [turns]);

  if (turns.length === 0) return null;
  return (
    <div ref={containerRef} className="chat-transcript" aria-live="polite">
      {turns.map((t) => (
        <div key={t.id} className={`chat-turn chat-turn--${t.role}`}>
          <span className="chat-turn__who">{t.role === 'learner' ? 'You' : COACH.firstName}</span>
          <p className="chat-turn__text">{t.text}</p>
        </div>
      ))}
    </div>
  );
}
